import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes, FaDownload, FaExpand, FaCompress } from 'react-icons/fa';
import './FlipbookViewer.css';

const FlipbookViewer = ({ brochure, onClose }) => {
  const [currentSpread, setCurrentSpread] = useState(0); // Spread index (0 = pages 0-1, 1 = pages 2-3, etc.)
  const [currentPage, setCurrentPage] = useState(0); // Single page index for mobile view
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showNavButtons, setShowNavButtons] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const pages = brochure.pages || [];
  const totalPages = pages.length || brochure.pageCount || 0;
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  
  // Calculate total spreads (2 pages per spread, except possibly the last one)
  const totalSpreads = Math.ceil(totalPages / 2);

  // Detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swipe detection for mobile
  const minSwipeDistance = 50; // Minimum distance for a swipe

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setShowNavButtons(true);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  // Auto-hide nav buttons on mobile after inactivity
  useEffect(() => {
    if (!isMobile) {
      setShowNavButtons(true);
      return;
    }

    let hideTimeout;
    const resetTimer = () => {
      setShowNavButtons(true);
      clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        setShowNavButtons(false);
      }, 3000); // Hide after 3 seconds of inactivity
    };

    resetTimer();

    // Show buttons on touch/click
    const handleInteraction = () => {
      resetTimer();
    };

    window.addEventListener('touchstart', handleInteraction);
    window.addEventListener('click', handleInteraction);

    return () => {
      clearTimeout(hideTimeout);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };
  }, [isMobile, currentPage, currentSpread]);

  useEffect(() => {
    setCurrentSpread(0);
    setCurrentPage(0);
    // Debug: Log brochure data
    console.log('FlipbookViewer - Brochure data:', {
      title: brochure.title,
      pages: brochure.pages,
      pageCount: brochure.pageCount,
      totalPages,
      totalSpreads,
      baseUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brochure]);

  // Get left and right page numbers for current spread
  const getSpreadPages = (spreadIndex) => {
    const leftPage = spreadIndex * 2;
    const rightPage = leftPage + 1;
    return {
      left: leftPage < totalPages ? leftPage : null,
      right: rightPage < totalPages ? rightPage : null,
    };
  };

  const handlePrevious = () => {
    if (isFlipping) return;
    
    if (isMobile) {
      // Mobile: single page navigation
      if (currentPage > 0) {
        setIsFlipping(true);
        setTimeout(() => {
          setCurrentPage(currentPage - 1);
          setIsFlipping(false);
        }, 300);
      }
    } else {
      // Desktop: spread navigation
      if (currentSpread > 0) {
        setIsFlipping(true);
        setTimeout(() => {
          setCurrentSpread(currentSpread - 1);
          setIsFlipping(false);
        }, 300);
      }
    }
  };

  const handleNext = () => {
    if (isFlipping) return;
    
    if (isMobile) {
      // Mobile: single page navigation
      if (currentPage < totalPages - 1) {
        setIsFlipping(true);
        setTimeout(() => {
          setCurrentPage(currentPage + 1);
          setIsFlipping(false);
        }, 300);
      }
    } else {
      // Desktop: spread navigation
      if (currentSpread < totalSpreads - 1) {
        setIsFlipping(true);
        setTimeout(() => {
          setCurrentSpread(currentSpread + 1);
          setIsFlipping(false);
        }, 300);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpread, currentPage, isFlipping, isMobile]);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Helper to get full image URL
  const getImageUrl = (pagePath) => {
    if (!pagePath) return '';
    // If it's already a full URL (S3), return it directly
    if (pagePath.startsWith('http')) return pagePath;
    // Otherwise, construct URL from relative path
    const cleanPath = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  const spreadPages = getSpreadPages(currentSpread);
  const currentPageNumbers = isMobile ? {
    current: currentPage + 1,
  } : {
    left: spreadPages.left !== null ? spreadPages.left + 1 : null,
    right: spreadPages.right !== null ? spreadPages.right + 1 : null,
  };

  return (
    <div className={`flipbook-viewer ${isFullscreen ? 'fullscreen' : ''} ${isFlipping ? 'flipping' : ''}`}>
      <div className="flipbook-header">
        <h3>{brochure.title}</h3>
        <div className="flipbook-actions">
          <button onClick={toggleFullscreen} className="btn-icon" title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
          {brochure.fileUrl && (
            <a
              href={brochure.fileUrl.startsWith('http') 
                ? brochure.fileUrl 
                : `${baseUrl}${brochure.fileUrl.startsWith('/') ? brochure.fileUrl : `/${brochure.fileUrl}`}`}
              download
              className="btn-icon"
              title="Download"
            >
              <FaDownload />
            </a>
          )}
          <button onClick={onClose} className="btn-icon" title="Close">
            <FaTimes />
          </button>
        </div>
      </div>

      <div className="flipbook-container">
        <button
          className={`flipbook-nav prev ${isMobile && !showNavButtons ? 'mobile-hidden' : ''}`}
          onClick={handlePrevious}
          disabled={(isMobile ? currentPage === 0 : currentSpread === 0) || isFlipping}
          title="Previous page"
          onTouchStart={(e) => {
            e.stopPropagation();
            setShowNavButtons(true);
          }}
        >
          <FaChevronLeft />
        </button>

        <div 
          className={`flipbook-spread-container ${isMobile ? 'mobile-view' : 'desktop-view'}`}
          onTouchStart={isMobile ? onTouchStart : undefined}
          onTouchMove={isMobile ? onTouchMove : undefined}
          onTouchEnd={isMobile ? onTouchEnd : undefined}
          onClick={() => isMobile && setShowNavButtons(true)}
        >
          {pages.length > 0 ? (
            isMobile ? (
              // Mobile: Single page view
              <div className="flipbook-single-page">
                <div className="flipbook-page-wrapper single-page">
                  <img
                    src={getImageUrl(pages[currentPage])}
                    alt={`Page ${currentPageNumbers.current}`}
                    className="flipbook-page"
                    onError={(e) => {
                      console.error('Failed to load page:', getImageUrl(pages[currentPage]));
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="page-number center">{currentPageNumbers.current}</div>
                </div>
              </div>
            ) : (
              // Desktop: Two-page spread view
              <div className="flipbook-spread">
                {/* Left Page */}
                <div className="flipbook-page-wrapper left-page">
                  {spreadPages.left !== null ? (
                    <img
                      src={getImageUrl(pages[spreadPages.left])}
                      alt={`Page ${currentPageNumbers.left}`}
                      className="flipbook-page"
                      onError={(e) => {
                        console.error('Failed to load left page:', getImageUrl(pages[spreadPages.left]));
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flipbook-page blank-page"></div>
                  )}
                  {spreadPages.left !== null && (
                    <div className="page-number left">{currentPageNumbers.left}</div>
                  )}
                </div>

                {/* Book Spine / Divider */}
                <div className="book-spine"></div>

                {/* Right Page */}
                <div className="flipbook-page-wrapper right-page">
                  {spreadPages.right !== null ? (
                    <img
                      src={getImageUrl(pages[spreadPages.right])}
                      alt={`Page ${currentPageNumbers.right}`}
                      className="flipbook-page"
                      onError={(e) => {
                        console.error('Failed to load right page:', getImageUrl(pages[spreadPages.right]));
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flipbook-page blank-page"></div>
                  )}
                  {spreadPages.right !== null && (
                    <div className="page-number right">{currentPageNumbers.right}</div>
                  )}
                </div>
              </div>
            )
          ) : (
            <div className="flipbook-page-placeholder">
              <p>No pages available. PDF processing may have failed.</p>
              {brochure.fileUrl && (
                <a 
                  href={brochure.fileUrl.startsWith('http') 
                    ? brochure.fileUrl 
                    : `${baseUrl}${brochure.fileUrl.startsWith('/') ? brochure.fileUrl : `/${brochure.fileUrl}`}`} 
                  download 
                  className="btn-download-link"
                >
                  Download PDF instead
                </a>
              )}
            </div>
          )}
        </div>

        <button
          className={`flipbook-nav next ${isMobile && !showNavButtons ? 'mobile-hidden' : ''}`}
          onClick={handleNext}
          disabled={(isMobile ? currentPage >= totalPages - 1 : currentSpread >= totalSpreads - 1) || isFlipping}
          title="Next page"
          onTouchStart={(e) => {
            e.stopPropagation();
            setShowNavButtons(true);
          }}
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="flipbook-footer">
        <div className="page-indicator">
          {isMobile ? (
            <>Page {currentPageNumbers.current} of {totalPages}</>
          ) : (
            <>
              {currentPageNumbers.left && currentPageNumbers.right ? (
                <>Pages {currentPageNumbers.left}-{currentPageNumbers.right} of {totalPages}</>
              ) : currentPageNumbers.left ? (
                <>Page {currentPageNumbers.left} of {totalPages}</>
              ) : (
                <>Page {currentPageNumbers.right} of {totalPages}</>
              )}
              <span className="spread-indicator">({currentSpread + 1} of {totalSpreads} spreads)</span>
            </>
          )}
        </div>
        <div className="page-thumbnails">
          {isMobile ? (
            // Mobile: Show individual page thumbnails
            pages.slice(0, 10).map((page, index) => (
              <button
                key={index}
                className={`thumbnail ${currentPage === index ? 'active' : ''}`}
                onClick={() => {
                  if (!isFlipping) {
                    setIsFlipping(true);
                    setTimeout(() => {
                      setCurrentPage(index);
                      setIsFlipping(false);
                    }, 300);
                  }
                }}
                title={`Go to page ${index + 1}`}
              >
                <img 
                  src={getImageUrl(page)} 
                  alt={`Page ${index + 1}`} 
                  onError={(e) => e.target.style.display = 'none'} 
                />
              </button>
            ))
          ) : (
            // Desktop: Show spread thumbnails
            Array.from({ length: Math.min(totalSpreads, 10) }).map((_, index) => {
              const spread = getSpreadPages(index);
              return (
                <button
                  key={index}
                  className={`thumbnail ${currentSpread === index ? 'active' : ''}`}
                  onClick={() => {
                    if (!isFlipping) {
                      setIsFlipping(true);
                      setTimeout(() => {
                        setCurrentSpread(index);
                        setIsFlipping(false);
                      }, 300);
                    }
                  }}
                  title={`Go to spread ${index + 1}`}
                >
                  {spread.left !== null && (
                    <img 
                      src={getImageUrl(pages[spread.left])} 
                      alt={`Spread ${index + 1}`} 
                      onError={(e) => e.target.style.display = 'none'} 
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default FlipbookViewer;

